import { Component, OnDestroy, OnInit } from "@angular/core";
import * as moment from "moment";
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
} from "@angular/forms";
import { TranslateService, LangChangeEvent } from "@ngx-translate/core";
import { BalanceService } from "../services/balance.service";
import { PrincipalService } from "../../../shared";
import { MaintenanceResourceService } from "../../../shared/services/maintenance-resource.service";
import { Account } from "../types/account.type";
import { Router } from "@angular/router";
import { IMyDpOptions, IMyDateModel } from "mydatepicker";
import { DocumentResourceService } from "../../../shared/services/document-resource.service";
import { KeypressValidator } from "../../../shared/validators/keypressValidator";
import { ROLE_CONSTANTS } from "../../../shared/constants/constant";

interface DatepickerAlert {
  status: boolean;
  message: string;
}

@Component({
  selector: "app-balance",
  templateUrl: "./balance.component.html",
  styleUrls: ["./balance.component.css"],
})
export class BalanceComponent implements OnInit, OnDestroy {
  private userRole: string;
  private langSubscription: any;
  // private account: Account;
  public lang: string;
  public inMaintenance;
  public progressIndicator;
  public searchString: string;
  public toDate;
  public fromDate;
  public datepickerAlert: DatepickerAlert;
  public balanceFilterForm: FormGroup;
  public fromDateYear: AbstractControl;
  public fromDateMonth: AbstractControl;
  public fromDateDay: AbstractControl;
  public toDateYear: AbstractControl;
  public toDateMonth: AbstractControl;
  public toDateDay: AbstractControl;
  public balanceStartDate: AbstractControl;
  public balanceEndDate: AbstractControl;
  public keypressValidator = KeypressValidator;
  public loadingIntervalPromise;
  public showUnitPrice: boolean;
  public unitPriceDate;
  public contribution;
  public fundCompositions;
  public fundPerformances;
  public emptySearchParam: boolean;
  public myDatePickerOptions: IMyDpOptions;
  public invalidDateRange: boolean;
  public employeeName: string;
  public certificateNumber;
  public serverError: boolean;
  public isAdmin: boolean;
  public isEmployee: boolean;

  private RESPONSE_WAITING_TIME = 120000;
  private MAINTENANCE_MESSAGE =
    "Currently system in maintenance, could not process balance.";
  public DATE_PICKER_ALERT_MESSAGE_FOR_EMPTY_YEAR = "Please choose year first";
  public DATE_PICKER_ALERT_MESSAGE_FOR_YEAR_LESS_THAN_1990 =
    "Cannot see from year under 1990";
  public DATE_PICKER_ALERT_MESSAGE_FOR_EMPTY_MONTH =
    "Please choose month first";
  public DATE_PICKER_ALERT_MESSAGE_FOR_MONTH_MORE_THAN_12 =
    "Only 12 months on a year";
  public DATE_PICKER_ALERT_MESSAGE_FOR_INVALID_DATE_OF_MONTH = (
    dateMonth,
    lastDay
  ) => {
    return "Month-" + dateMonth + " only has " + lastDay + " days";
  };
  public PROGRESS = {
    LOADING: "loading",
    TIMEOUT: "timeout",
    NODATA: "nodata",
    LOADED: "loaded",
    EMPTY: "",
  };

  constructor(
    private balanceService: BalanceService,
    private documentResourceService: DocumentResourceService,
    private maintenanceResourceService: MaintenanceResourceService,
    public principalService: PrincipalService,
    private translate: TranslateService,
    public router: Router
  ) {
    this.searchString = "";
    this.lang = translate.currentLang;
    this.langSubscription = this.translate.onLangChange.subscribe(
      (event: LangChangeEvent) => {
        this.lang = event.lang;
      }
    );
    this.isAdmin = false;
    this.isEmployee = false;
    this.inMaintenance = false;

    this.loadingIntervalPromise = null;
    this.datepickerAlert = {
      status: false,
      message: "",
    };
    this.balanceFilterForm = new FormGroup({
      fromDateYear: new FormControl("", [
        Validators.required,
        Validators.minLength(4),
      ]),
      fromDateMonth: new FormControl("", Validators.required),
      fromDateDay: new FormControl(
        { value: "", disabled: this.datepickerAlert.status },
        Validators.required
      ),
      toDateYear: new FormControl("", [
        Validators.required,
        Validators.minLength(4),
      ]),
      toDateMonth: new FormControl("", Validators.required),
      toDateDay: new FormControl("", Validators.required),
      balanceStartDate: new FormControl("", Validators.required),
      balanceEndDate: new FormControl("", Validators.required),
    });
    this.fromDateYear = this.balanceFilterForm.get("fromDateYear");
    this.fromDateMonth = this.balanceFilterForm.get("fromDateMonth");
    this.fromDateDay = this.balanceFilterForm.get("fromDateDay");
    this.toDateYear = this.balanceFilterForm.get("toDateYear");
    this.toDateMonth = this.balanceFilterForm.get("toDateMonth");
    this.toDateDay = this.balanceFilterForm.get("toDateDay");
    this.balanceStartDate = this.balanceFilterForm.get("balanceStartDate");
    this.balanceEndDate = this.balanceFilterForm.get("balanceEndDate");

    this.toDate = moment().toDate();
    this.toDateYear.patchValue(moment(this.toDate).year());
    this.toDateMonth.patchValue(moment(this.toDate).month() + 1);
    this.toDateDay.patchValue(moment(this.toDate).date());
    this.fromDate = moment(this.toDate).add(-7, "days").toDate();
    this.fromDateYear.patchValue(moment(this.fromDate).year());
    this.fromDateMonth.patchValue(moment(this.fromDate).month() + 1);
    this.fromDateDay.patchValue(moment(this.fromDate).date());
    this.balanceStartDate.patchValue(
      this.datepickerFormat(
        this.fromDateYear.value,
        this.fromDateMonth.value,
        this.fromDateDay.value
      )
    );
    this.balanceEndDate.patchValue(
      this.datepickerFormat(
        this.toDateYear.value,
        this.toDateMonth.value,
        this.toDateDay.value
      )
    );

    this.myDatePickerOptions = {
      dateFormat: "dd-mmm-yyyy",
      showTodayBtn: true,
      minYear: 1990,
      yearSelector: false,
      markCurrentDay: true,
      showSelectorArrow: true,
      showClearDateBtn: false,
      editableDateField: true,
      disableSince: {
        year: moment().year(), // this year
        month: moment().get("month") + 1, // this month
        day: moment().date() + 1, // tommorrow
      },
    };

    this.emptySearchParam = true;
    this.showUnitPrice = false;
    this.serverError = false;

    try {
      this.balanceService.receive().subscribe((data) => {
        if (Object.keys(data).length == 0) {
          this.progressIndicator = this.PROGRESS.NODATA;
        } else {
          this.progressIndicator = this.PROGRESS.LOADED;
        }
        if (this.loadingIntervalPromise) {
          clearInterval(this.loadingIntervalPromise);
        }
        this.onResponseReceived(data);
      });
    } catch (e) {}
  }

  ngOnInit() {
    this.principalService.identity(true).then(
      (account: Account) => {
        if (account == null) {
          this.notLoggedInAction();
        } else {
          this.balanceInformation(account);
        }
      },
      (error) => {
        this.notLoggedInAction();
      }
    );
  }

  notLoggedInAction() {
    this.serverError = true;
    this.router.navigate(["login"]);
  }

  balanceInformation(account: Account) {
    this.employeeName = account["participantName"];
    this.certificateNumber = account["login"];

    this.userRole = account.authorities[0];
    if (this.userRole == ROLE_CONSTANTS.ROLE_EMPLOYEE) {
      this.isEmployee = true;
      this.emptySearchParam = false;
      // this.requestBalance();
    }
    if (
      this.userRole == ROLE_CONSTANTS.ROLE_EMPLOYER ||
      this.userRole == ROLE_CONSTANTS.ROLE_ADMIN ||
      this.userRole == ROLE_CONSTANTS.ROLE_SUPPORT
    ) {
      this.isAdmin = true;
    }
    // Added by Rizal
    // Employeer of Pooled Fund product have certificate number. Auto call Balance API
    if (
      this.userRole == ROLE_CONSTANTS.ROLE_EMPLOYEE ||
      this.userRole == ROLE_CONSTANTS.ROLE_EMPLOYER
    ) {
      this.requestBalance();
    }
  }

  ngOnDestroy() {
    this.langSubscription.unsubscribe();
    clearInterval(this.loadingIntervalPromise);
  }

  onFromDateCalendarChanged(event: IMyDateModel): void {
    const selectedDate = event.date;
    this.fromDateYear.patchValue(selectedDate.year);
    this.fromDateMonth.patchValue(selectedDate.month);
    this.fromDateDay.patchValue(selectedDate.day);
    this.setFromDate();
  }

  onToDateCalendarChanged(event: IMyDateModel): void {
    const selectedDate = event.date;
    this.toDateYear.patchValue(selectedDate.year);
    this.toDateMonth.patchValue(selectedDate.month);
    this.toDateDay.patchValue(selectedDate.day);
    this.setToDate();
  }

  setFromDate() {
    this.checkingDateInput(
      this.fromDateDay.value,
      this.fromDateMonth.value,
      this.fromDateYear.value,
      "fromDate"
    );
  }

  setToDate() {
    this.checkingDateInput(
      this.toDateDay.value,
      this.toDateMonth.value,
      this.toDateYear.value,
      "toDate"
    );
  }

  checkingDateInput(dateDay, dateMonth, dateYear, type) {
    this.datepickerAlert.status = false;
    this.datepickerAlert.message = "";

    this.checkingDateInputIsAlreadyValid(dateDay, dateMonth, dateYear, type);
  }

  checkingDateInputIsAlreadyValid(dateDay, dateMonth, dateYear, type) {
    if ((dateDay != "" || dateMonth != "") && dateYear == "") {
      this.datepickerAlert.status = true;
      this.datepickerAlert.message =
        this.DATE_PICKER_ALERT_MESSAGE_FOR_EMPTY_YEAR;
      if (type === "fromDate") {
        this.fromDateMonth.patchValue("");
        this.fromDateDay.patchValue("");
      } else if (type === "toDate") {
        this.toDateMonth.patchValue("");
        this.toDateDay.patchValue("");
      }
    } else {
      this.checkingDateInputOnlyMoreThan1990(
        dateDay,
        dateMonth,
        dateYear,
        type
      );
    }
  }

  checkingDateInputOnlyMoreThan1990(dateDay, dateMonth, dateYear, type) {
    if (dateYear < 1990) {
      this.datepickerAlert.status = true;
      this.datepickerAlert.message =
        this.DATE_PICKER_ALERT_MESSAGE_FOR_YEAR_LESS_THAN_1990;
    } else {
      this.checkingDateInputChooseMonthBeforeInputDay(
        dateDay,
        dateMonth,
        dateYear,
        type
      );
    }
  }

  checkingDateInputChooseMonthBeforeInputDay(
    dateDay,
    dateMonth,
    dateYear,
    type
  ) {
    if (dateDay != "" && dateMonth == "") {
      this.datepickerAlert.status = true;
      this.datepickerAlert.message =
        this.DATE_PICKER_ALERT_MESSAGE_FOR_EMPTY_MONTH;
      if (type === "fromDate") {
        this.fromDateDay.patchValue("");
      } else if (type === "toDate") {
        this.toDateDay.patchValue("");
      }
    } else {
      this.checkingDateInputHasValidMonths(dateDay, dateMonth, dateYear, type);
    }
  }

  checkingDateInputHasValidMonths(dateDay, dateMonth, dateYear, type) {
    if (dateMonth > 12) {
      this.datepickerAlert.status = true;
      this.datepickerAlert.message =
        this.DATE_PICKER_ALERT_MESSAGE_FOR_MONTH_MORE_THAN_12;
      if (type === "fromDate") {
        this.fromDateMonth.patchValue("");
      } else if (type === "toDate") {
        this.toDateMonth.patchValue("");
      }
    } else {
      this.checkingDateInputHasValidDate(dateDay, dateMonth, dateYear, type);
    }
  }

  checkingDateInputHasValidDate(dateDay, dateMonth, dateYear, type) {
    const currentMonthDate = moment(
      [dateYear, parseInt(dateMonth), 1],
      "YYYY-MM-DD"
    ).toDate();
    const nextMonth = moment(currentMonthDate).add(1, "month").toDate();
    const lastDateOfFilledMonth = moment(nextMonth).add(-1, "day").toDate();
    const lastDay = moment(lastDateOfFilledMonth).format("D");
    if (parseInt(dateDay) > parseInt(lastDay)) {
      this.datepickerAlert.status = true;
      this.datepickerAlert.message =
        this.DATE_PICKER_ALERT_MESSAGE_FOR_INVALID_DATE_OF_MONTH(
          dateMonth,
          lastDay
        );
      if (type === "fromDate") {
        this.fromDateDay.patchValue("");
      } else if (type === "toDate") {
        this.toDateDay.patchValue("");
      }
    } else {
      this.updateChangesAfterCheckingDateInput(
        dateDay,
        dateMonth,
        dateYear,
        type
      );
    }
  }

  updateChangesAfterCheckingDateInput(dateDay, dateMonth, dateYear, type) {
    const changedDate = moment(
      dateYear + "-" + dateMonth + "-" + dateDay,
      "YYYY-MM-DD"
    ).toDate();
    const datepickerChangedValue = this.datepickerFormat(
      dateYear,
      dateMonth,
      dateDay
    );
    if (type === "fromDate") {
      this.fromDate = changedDate;
      this.balanceStartDate.patchValue(datepickerChangedValue);
    } else if (type === "toDate") {
      this.toDate = changedDate;
      this.balanceEndDate.patchValue(datepickerChangedValue);
    }
  }

  datepickerFormat(year, month, day) {
    return { date: { year: year, month: month, day: day } };
  }

  clearSearchData() {
    if (!this.isEmployee) {
      this.emptySearchParam = false;
    }
    this.toDateYear.patchValue("");
    this.toDateMonth.patchValue("");
    this.toDateDay.patchValue("");
    this.fromDateYear.patchValue("");
    this.fromDateMonth.patchValue("");
    this.fromDateDay.patchValue("");
    this.balanceStartDate.patchValue("");
    this.balanceEndDate.patchValue("");
    this.searchString = "";
    this.invalidDateRange = false;
    this.progressIndicator = this.PROGRESS.EMPTY;
  }

  checkSearchString() {
    this.emptySearchParam =
      this.searchString === "" || this.searchString === undefined;
    return;
  }

  downloadBalance() {
    const startDate = moment(this.fromDate).format("YYYY-MM-DD");
    const endDate = moment(this.toDate).format("YYYY-MM-DD");
    this.documentResourceService.downloadBenefitStatement(
      this.searchString,
      startDate,
      endDate,
      this.lang
    );
  }

  requestBalance() {
    if (this.loadingIntervalPromise) {
      clearInterval(this.loadingIntervalPromise);
    }
    
    // execute if inmaintenace false
    if (this.inMaintenance === false) {
      this.maintenanceResourceService
        .inMaintenance()
        .toPromise()
        .then((data) => {
          this.inMaintenance = data;
          if (this.inMaintenance) {
            console.log(this.MAINTENANCE_MESSAGE);
            return;
          } else {
            this.inMaintenance = false;
            const startDate = moment(this.fromDate).format("YYYY-MM-DD");
            const endDate = moment(this.toDate).format("YYYY-MM-DD");
            if (moment(this.toDate).isBefore(moment(this.fromDate))) {
              this.invalidDateRange = true;
              this.progressIndicator = this.PROGRESS.EMPTY;
            } else {
              this.progressIndicator = this.PROGRESS.LOADING;
              this.initResponseTimeoutHandler();
              this.invalidDateRange = false;

              this.balanceService.connect(
                this.searchString,
                startDate,
                endDate,
                this.lang
              );
              this.balanceService.subscribe(null);
            }
          }
        });
    } else {
      console.log(this.inMaintenance);
    }
  }

  initResponseTimeoutHandler() {
    this.loadingIntervalPromise = setInterval(() => {
      if (this.progressIndicator === this.PROGRESS.LOADING) {
        console.log(
          "2 minutes waiting for balance response is over.. Show timeout message!"
        );
        this.progressIndicator = this.PROGRESS.TIMEOUT;
      }
    }, this.RESPONSE_WAITING_TIME);
  }

  public onResponseReceived(data) {
    if (Object.keys(data).length == 0) {
      this.progressIndicator = this.PROGRESS.NODATA;
    } else {
      this.progressIndicator = this.PROGRESS.LOADED;
      this.contribution = data.contribution;
      this.fundCompositions = data.fundComposition;
      this.fundPerformances = data.fundPerformance;
      if (data.unitDate !== null && data.unitDate !== undefined) {
        this.showUnitPrice = true;
        const responseUnitPriceDate = moment(
          data.unitDate,
          "YYYY-MM-DD"
        ).toDate();
        this.unitPriceDate = moment(responseUnitPriceDate).format(
          "DD-MMM-YYYY"
        );
      }
    }
  }
}
